import { useMemo } from 'react';
import { Calendar, AlertCircle } from 'lucide-react';
import { useTickets } from '@/hooks/useProduction';
import { Card } from '@/components/ui/Card';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { TicketCard } from '@/components/production/TicketCard';

function bucketFor(dueDate?: string): string {
  if (!dueDate) return 'No due date';
  const due = new Date(dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.floor((due.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
  if (diff < 0) return 'Past Due';
  if (diff === 0) return 'Due Today';
  if (diff === 1) return 'Tomorrow';
  if (diff <= 7) return 'This Week';
  if (diff <= 14) return 'Next Week';
  if (diff <= 30) return 'This Month';
  return 'Later';
}

const ORDER = [
  'Past Due', 'Due Today', 'Tomorrow', 'This Week',
  'Next Week', 'This Month', 'Later', 'No due date',
];

export function SchedulePage() {
  const { data, isLoading, error } = useTickets({ limit: 500 });

  const grouped = useMemo(() => {
    if (!data) return new Map<string, typeof data>();
    const out = new Map<string, typeof data>();
    for (const bucket of ORDER) out.set(bucket, []);
    for (const t of data) {
      if (['complete', 'cancelled'].includes(t.status)) continue;
      const b = bucketFor(t.dueDate);
      out.get(b)!.push(t);
    }
    // Sort within each bucket by due date asc
    out.forEach((tickets) => {
      tickets.sort((a, b) => {
        const da = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
        const db = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
        return da - db;
      });
    });
    return out;
  }, [data]);

  if (isLoading) return <LoaderScreen />;
  if (error) {
    return (
      <Card>
        <EmptyState
          icon={AlertCircle}
          title="Could not load schedule"
          description={(error as { message?: string })?.message ?? 'An unexpected error occurred.'}
        />
      </Card>
    );
  }

  const nonEmpty = ORDER.filter((b) => (grouped.get(b)?.length ?? 0) > 0);

  if (nonEmpty.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={Calendar}
          title="Nothing scheduled"
          description="All tickets are either complete or cancelled. Release a ticket to the floor to see it here."
        />
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {nonEmpty.map((bucket) => {
        const tickets = grouped.get(bucket) ?? [];
        const isPastDue = bucket === 'Past Due';
        return (
          <div key={bucket}>
            <div className={`flex items-baseline justify-between mb-3 pb-2 border-b ${
              isPastDue ? 'border-danger/30' : 'border-border'
            }`}>
              <div>
                <div className={`kicker ${isPastDue ? 'text-danger' : ''}`}>{bucket}</div>
              </div>
              <div className="text-2xs text-text-dim tabular">
                {tickets.length} ticket{tickets.length === 1 ? '' : 's'}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {tickets.map((t) => <TicketCard key={t.id} ticket={t} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}
